import Bottombar from "@/components/bottombar"
import Button from "@/components/button"
import FormulaFormView from "@/components/formula-form-view"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { UseFormReturn, useWatch } from "@/lib/form/form"
import { ParamSchema } from "@/lib/form/schema"
import { useEffect, useState } from "react"
import { evaluate, format } from "mathjs"

export interface FormualDialogProps {
  form: UseFormReturn
}

export function FormulaDialog({ form }: FormualDialogProps) {
  const [open, setOpen] = useState(false)
  const [touched, setTouched] = useState(false)
  const [path, setPath] = useState("")
  const [schema, setSchema] = useState<ParamSchema | null>(null)
  const [localFormValue, setLocalFormValue] = useState<number[]>([])
  const result = useResult()
  const formValue = useWatch({
    form,
    name: path,
  })

  useInit()

  function useInit() {
    useEffect(() => {
      form.addEventHandler("showFormulaInput", (__e, ctx) => {
        setSchema(ctx.schema as ParamSchema)
        setPath(ctx.path)
        setOpen(true)

        const value = form.getValues(ctx.path)
        setLocalFormValue(value.inputs || [])
      })

      setLocalFormValue(formValue?.inputs?.length ? formValue.inputs : [])
    }, [form])

    useEffect(() => {
      if (!open) {
        setTimeout(() => {
          setLocalFormValue([])
          setTouched(false)
        }, 500)
      }
    }, [open])
  }

  function useResult() {
    const [result, setResult] = useState<string>("0")

    useEffect(() => {
      if (!schema?.formula) {
        return
      }

      try {
        const payload: Record<string, number> = {}

        for (const [index, value] of localFormValue.entries()) {
          const variable = schema.formula.inputs[index].variable
          payload[`$${variable}`] = value
        }

        const newResult = format(
          evaluate(schema.formula.formulaText, payload),
          { precision: 2, notation: "fixed" }
        )
        setResult(newResult)
      } catch {
        setResult("0")
      }
    }, [localFormValue])

    return result
  }

  function formatLabel() {
    if (!schema?.unit) {
      return schema?.name || ""
    }

    return `${schema.name} (${schema.unit})`
  }

  function handleClose() {
    setPath("")
    setOpen(false)
  }

  function handleChangeFormulaInputs(values: number[]) {
    setTouched(true)
    setLocalFormValue(values)
  }

  function handleSubmit() {
    form.setValue(
      path,
      {
        id: schema?.id,
        value: result,
        recordedAt: new Date().toISOString(),
        inputs: localFormValue,
      },
      {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      }
    )
    handleClose()
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="p-0 overflow-hidden">
        <DialogTitle className="font-semibold text-xl text-button-black px-4 pt-6">
          {formatLabel()}
        </DialogTitle>

        <FormulaFormView
          className="px-4 pt-6"
          schema={schema?.formula}
          value={formValue?.inputs}
          onChange={handleChangeFormulaInputs}
        />

        <Bottombar className="flex gap-4 mt-4 bg-white">
          <Button variant="outline" onClick={handleClose}>
            Back
          </Button>
          <Button
            variant="default"
            className="flex-1"
            disabled={!touched}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </Bottombar>
      </DialogContent>
    </Dialog>
  )
}
